import React, { useState, useEffect, useRef } from "react";
import Icon from "../../components/ui/icon";
import SideNav from "../../components/layout/sideNav";
import { useToast } from "../../context/toastContext";
import { useNavigate } from "react-router-dom";
import api from "../../services/apiClient";
import "../../assets/styles/pages/publishers/campaigns.css";
import DOMPurify from "dompurify";
import { convertCurrency } from "../../utils/currencyConverter";
import { storageManager } from "../../utils/storageManager";
import countryOptions from "../../utils/countryOptions";

const CampaignsPage = () => {
  const navigate = useNavigate();
  const showToast = useToast();
  const [isSideNavActive, setisSideNavActive] = useState(false);
  const [userInfo, setUserInfo] = useState(
    JSON.parse(localStorage.getItem("PublisherInfo")) || {}
  );
  const [campaigns, setCampaigns] = useState([]);
  const [campaignsFetchStatus, setCampaignsFetchStatus] =
    useState("fetching_first");
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [userVerified, setUserVerified] = useState(false);
  const limit = 20;
  const [campaignSearchText, setCampaignSearchText] = useState("");
  const [isSearchingCamps, setIsSearchingCamps] = useState(false);
  const [isShowingSearchResults, setIsShowingSearchResults] = useState(false);
  const isLoadingMore = useRef(false);
  const debounceRef = useRef(null);
  const currency = storageManager.getCurrency();

  useEffect(() => {
    const token = localStorage.getItem("aToken");
    if (!token) {
      navigate("/auth/login");
      return;
    }

    const validateUser = async () => {
      if (userVerified) return;

      api
        .get(`/api/publisher/info`)
        .then((response) => {
          setUserInfo(response.data.data);
          setUserVerified(true);
          localStorage.setItem(
            "PublisherInfo",
            JSON.stringify(response.data.data)
          );
          fetchCampaigns();
        })
        .catch((err) => {
          showToast({
            status: "error",
            message: err.response?.data?.message || "Login not found.",
          });
          navigate("/auth/login");
        });
    };

    const fetchCampaigns = () => {
      const token = localStorage.getItem("aToken");

      if (!token) {
        console.error("No auth token found");
        setCampaignsFetchStatus("failed");
        return;
      }

      if (campaignsFetchStatus === "fetching" || !hasMore) return;
      if (campaignsFetchStatus !== "fetching_first")
        setCampaignsFetchStatus("fetching");

      api
        .get(`/api/campaign?limit=${limit}&skip=${skip}`)
        .then((response) => {
          if (response.data.status) {
            const fetchedCampaigns = response.data.campaigns;

            if (fetchedCampaigns.length < limit) {
              setHasMore(false);
              setCampaignsFetchStatus("not_needed");
            }

            isLoadingMore.current = false;

            setCampaigns((prev) => [...prev, ...fetchedCampaigns]);
            setSkip((prevSkip) => prevSkip + limit);
            setCampaignsFetchStatus("success");
          } else {
            setCampaignsFetchStatus("failed");
          }
        })
        .catch((err) => {
          console.error(err);
          setCampaignsFetchStatus("failed");
        });
    };

    const handleScroll = () => {
      const scrollPosition = window.innerHeight + window.scrollY;
      const threshold = document.documentElement.scrollHeight - 300;

      if (scrollPosition >= threshold && hasMore) {
        if (campaignsFetchStatus === "fetching_first" || campaignsFetchStatus === "not_needed") return;
        if (isShowingSearchResults) return;
        if (isSearchingCamps) return;
        if (isLoadingMore.current) return;
        isLoadingMore.current = true;
        fetchCampaigns();
      }
    };

    validateUser();

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [
    campaignsFetchStatus,
    hasMore,
    navigate,
    showToast,
    skip,
    userVerified,
    isShowingSearchResults,
    isSearchingCamps,
  ]);

  const sanitizeInput = (value) => DOMPurify.sanitize(value);

  const handelCampSearchInpChnage = (e) => {
    const sText = sanitizeInput(e.target.value);
    setCampaignSearchText(sText);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (sText.length === 0) {
      setCampaigns([]);
      setSkip(0);
      setHasMore(true);
      setIsSearchingCamps(false);
      setCampaignsFetchStatus("fetching_first");
      api
        .get(`/api/campaign?limit=${limit}&skip=0`)
        .then((response) => {
          if (response.data.status) {
            const fetchedCampaigns = response.data.campaigns;

            if (fetchedCampaigns.length < limit) {
              setHasMore(false);
              setCampaignsFetchStatus("not_needed");
            }

            setCampaigns((prev) => [...prev, ...fetchedCampaigns]);
            setSkip((prevSkip) => prevSkip + limit);
            setCampaignsFetchStatus("success");
            setIsShowingSearchResults(false);
          } else {
            setCampaignsFetchStatus("failed");
          }
        })
        .catch((err) => {
          console.error(err);
          setCampaignsFetchStatus("failed");
        });
    }

    if (sText.length < 3 && sText.length > 0) {
      setIsSearchingCamps(false);
      return;
    }

    if (sText.length >= 3) {
      setIsSearchingCamps(true);

      debounceRef.current = setTimeout(() => {
        api
          .get(`/api/campaign/search?searchText=${sText}&filter=all`)
          .then((response) => {
            if (response.data.status) {
              setCampaigns(response.data.campaigns);
              setIsShowingSearchResults(true);
            }
          })
          .catch((err) => {
            showToast({
              status: "error",
              message:
                err.response?.data?.message ||
                "Something went wrong while searching.",
            });
          })
          .finally(() => {
            setIsSearchingCamps(false);
          });
      }, 1500);
    }
  };

  const getLabel = (value) => {
    const item = countryOptions.find(c => c.value === value);
    return item ? `${item.flag} ${item.code}` : value;
  };

  const getFlag = (value) => {
    const item = countryOptions.find(c => c.value === value);
    return item ? `${item.flag}` : value;
  };

  return (
    <div className="campaigns__page">
      <SideNav
        userName={userInfo.name}
        emailID={userInfo.email}
        active={isSideNavActive}
        campaignOpenInitial={true}
        accManagerName={userInfo.adminInfo?.name}
        accManagerEmail={userInfo.adminInfo?.email}
      />
      <div
        onClick={() => setisSideNavActive(!isSideNavActive)}
        className={
          isSideNavActive ? "background-shadow active" : "background-shadow"
        }
      ></div>
      <main>
        <header className="desktop-header">
          <div>
            <h2>Campaigns</h2>
            <p>You can checkout out campaigns from here.</p>
          </div>
          <button>
            All <Icon name="ChevronDown" className="onRight" />
          </button>
        </header>

        <header className="mobile-header">
          <div>
            <h2>Campaigns</h2>
          </div>
          <button
            className="nav-btn"
            onClick={() => setisSideNavActive(!isSideNavActive)}
          >
            {isSideNavActive ? (
              <Icon name="XMark" className="onLeft" />
            ) : (
              <Icon name="Bars3" className="onLeft" />
            )}
          </button>
        </header>

        <div className="search-box">
          <Icon name="MagnifyingGlass" />
          <input
            type="text"
            placeholder="Search for campaigns by name eg: Swift Digital Ads"
            autoComplete="off"
            onChange={handelCampSearchInpChnage}
            value={campaignSearchText}
          />
        </div>

        <div className="table-container">
          <div className="top-section">
            <div>Name</div>
            <div>Type</div>
            <div>Payout</div>
            <div>Category</div>
            <div>Location</div>
            <div>Status</div>
            <div>Action</div>
          </div>

          {campaignsFetchStatus === "failed" ? (
            <div className="flat-message">
              <Icon name="ExclamationCircle" className="msg-icon" />
              <h3 className="title">Campaigns Retrieval Failed</h3>
              <p className="description">
                We encountered an error while trying to fetch campaigns. Please
                try again later.
              </p>
            </div>
          ) : campaigns && campaigns.length > 0 && !isSearchingCamps ? (
            campaigns.map((campaign, index) => {
              return (
                <div className="datas" key={campaign.campaignId}>
                  <div className="data campaign-name">
                    <img
                      src={
                        campaign.logo ? campaign.logo : "../DefaultImage.png"
                      }
                      alt="logo"
                      className="logo"
                    />
                    <div>
                      <span title={campaign.name}>{campaign.name}</span>
                      <span>{campaign.campaignId}</span>
                    </div>
                  </div>
                  <div className="data">{campaign.type}</div>
                  <div className="data">
                    {convertCurrency(
                      campaign?.goals
                        ?.filter((goal) => goal.isMain === true)
                        ?.map((goal) => goal.value)
                        .join(", "),
                      campaign?.goals
                        ?.filter((goal) => goal.isMain === true)
                        ?.map((goal) => goal.currency)
                        .join(", "),
                      currency
                    ) || "₹0"}
                  </div>
                  <div className="data">{campaign.category}</div>
                  <div className="data">
                    <div className={`cn-container ${campaign.allowed_locations.length === 1 ? "" : "multiple"}`}>
                      {
                        campaign.allowed_locations.map((location, index) => {
                          if (index >= 4) return null;
                          return (
                            <div className={index < 3 ? "cn" : "more-cn"} key={location}>
                              {index < 3 ? campaign.allowed_locations.length === 1 ? getLabel(location) : getFlag(location) : ""}
                              {index === 3 && campaign.allowed_locations.length > 3 ? `+${campaign.allowed_locations.length - 3}` : null}
                            </div>
                          )
                        })
                      }
                    </div>
                  </div>
                  <div className="data">
                    <span className={`status ${campaign.status}`}>
                      {campaign.status === "active"
                        ? "Live"
                        : campaign.status === "paused"
                          ? "Paused"
                          : "Expired"}
                    </span>
                  </div>
                  <div className="data">
                    <button
                      onClick={() => navigate(`/campaign/${campaign.campaignId}`)}
                    >
                      More info<Icon name="ChevronRight" />
                    </button>
                  </div>
                </div>
              );
            })
          ) : campaignsFetchStatus !== "fetching" &&
            campaignsFetchStatus !== "fetching_first" &&
            !isSearchingCamps ? (
            <div className="flat-message">
              <Icon name="ListBullet" className="msg-icon" />
              <h3 className="title">No Campaign Available</h3>
              <p className="description">
                Opps! Seems like we dont have any campaign at this moment.
              </p>
            </div>
          ) : (
            ""
          )}
        </div>
        {campaignsFetchStatus === "fetching" ||
          campaignsFetchStatus === "fetching_first" ||
          isSearchingCamps ? (
          <span className="bottom-fetch">
            <Icon name="ArrowPath" />{" "}
            {isSearchingCamps
              ? "Searching..."
              : campaignsFetchStatus === "fetching_first"
                ? "Fetching..."
                : "Fetching more..."}
          </span>
        ) : (
          ""
        )}

        <span className="copyright-text">
          © 2025 Swift Digital Ads, All rights reserved
        </span>
      </main>
    </div>
  );
};

export default CampaignsPage;
